import { Box } from '@mui/material';
import { useMediaQuery, useTheme } from '@rabex-kit/rabex-ui';
import { useLocation } from 'react-router-dom';
import SplashBackdrop from '@/components/splash/SplashBackdrop';
import AnimatedRabexMark from '@/components/splash/AnimatedRabexMark';
import { getSplashColors } from '@/components/splash/splashColors';
import { hasAppBooted } from './bootState';
import {
  Chrome,
  DEFAULT_CHROME,
  ResponsivePageSkeleton,
  SkeletonVariant
} from './PageSkeleton';
import * as routes from '@/pages/routes';

/**
 * The tab being opened, read off the URL the router has already switched to.
 * Longest prefixes first: the wallet path sits under /fa/my, and the dashboard
 * is /fa/ — a prefix of everything — so it is only matched exactly.
 */
export const variantForPath = (pathname: string): SkeletonVariant => {
  // Under the wallet path, so they have to be matched before it.
  if (pathname.startsWith(routes.WALLET_HISTORY)) return 'history';
  if (
    pathname.startsWith(`${routes.WALLET}/deposit`) ||
    pathname.startsWith(`${routes.WALLET}/withdraw`)
  )
    return 'form';
  if (
    pathname.startsWith(routes.WALLET) ||
    pathname.startsWith('/fa/my/wallet')
  )
    return 'wallet';
  if (pathname.startsWith(routes.PORTFOLIO)) return 'wallet';
  if (pathname.startsWith(routes.DIGITAL_GOLD)) return 'gold';
  if (pathname.startsWith(routes.RWA_ASSETS)) return 'rwa';
  if (pathname.startsWith(routes.MARKETS)) return 'markets';
  // The trading page itself (/fa/trade/BTCTMN); market detail and the
  // Android chart under the same prefix keep the generic trade shape.
  if (/^\/fa\/trade\/[^/]+\/?$/.test(pathname)) return 'p2p';
  if (pathname.startsWith(routes.P2P_STATIC)) return 'trade';
  if (pathname.startsWith(routes.OTC)) return 'convert';
  if (pathname === routes.DASHBOARD || pathname === '/fa') return 'dashboard';
  return 'generic';
};

/**
 * The frame each page draws around its content, mirrored from the layout props
 * the page passes to DashboardLayout (hasMobileHeader, hasFooter, sideBarMode)
 * — so the skeleton's header, tab bar and sidebar are where the real ones will
 * be. Anything not listed gets the default title bar and tab bar.
 */
export const chromeForPath = (pathname: string): Chrome => {
  const own = (mobileBar: Chrome['mobileBar'], footer = true): Chrome => ({
    ...DEFAULT_CHROME,
    mobileBar,
    footer
  });

  // The history pages draw their own back header on mobile, with no tab bar.
  if (pathname.startsWith(routes.WALLET_HISTORY))
    return { mobileBar: 'back', footer: false, sidebar: 'wallet' };
  // Deposit / withdraw: the page's own header, no sidebar.
  if (
    pathname.startsWith(`${routes.WALLET}/deposit`) ||
    pathname.startsWith(`${routes.WALLET}/withdraw`)
  )
    return own('none');
  // The portfolio draws its own top bar (back arrow, title, filters).
  if (pathname.startsWith(routes.PORTFOLIO))
    return { ...own('back'), sidebar: 'wallet' };
  // The wallet starts at its balance card; it has no top bar at all.
  if (pathname.startsWith(routes.WALLET))
    return { ...own('none'), sidebar: 'wallet' };
  if (pathname.startsWith(routes.PRICE_ALERT))
    return { ...own('none'), sidebar: 'profile' };
  if (
    pathname.startsWith(routes.MARKETS) ||
    pathname.startsWith(routes.P2P_STATIC) ||
    pathname.startsWith(routes.OTC) ||
    pathname === routes.DASHBOARD ||
    pathname === '/fa'
  )
    return own('none');
  // Gold and RWA hide the tab bar.
  if (
    pathname.startsWith(routes.DIGITAL_GOLD) ||
    pathname.startsWith(routes.RWA_ASSETS)
  )
    return own('title', false);
  // Account pages: title bar and tab bar on mobile, the profile sidebar beside
  // the content on desktop. KYC and the reward center have none.
  if (
    pathname.startsWith(routes.KYC) ||
    pathname.startsWith(routes.REWARD_CENTER)
  )
    return own('title');
  if (pathname.startsWith('/fa/my') || pathname.startsWith(routes.REFERRAL))
    return { ...own('title'), sidebar: 'profile' };
  return DEFAULT_CHROME;
};

/**
 * RouteFallback
 *
 * Suspense fallback for lazy routes. Pixel-identical to the index.html
 * preboot — same trapezoids, same shimmer, same animated mark — so when
 * React mounts and replaces #root's children, there's no visible swap
 * between the static splash and the React-rendered fallback.
 *
 * Lives in the entry chunk by design: it has to render before any other
 * route code exists. Stays cheap because the backdrop is pure CSS and
 * the mark is two inline SVG paths plus three keyframes — no spinner
 * library, no theme reads, nothing async in the render path.
 *
 * Only for the cold start. Once the first route has committed the splash has
 * done its job, and showing it again on a later navigation reads as the app
 * restarting.
 *
 * After that, every route change shows a shimmer in the shape of the page being
 * opened. Each page mounts its own header, so the wide layout loses its chrome
 * during a navigation just as the narrow one does — a blank screen there reads
 * as a crash, so it gets a header-and-body skeleton too.
 */
const RouteFallback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { pathname } = useLocation();

  if (hasAppBooted()) {
    return (
      <ResponsivePageSkeleton
        layout={isMobile ? 'mobile' : 'desktop'}
        variant={variantForPath(pathname)}
        chrome={chromeForPath(pathname)}
      />
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="در حال بارگذاری"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '100vh',
        bgcolor: getSplashColors(theme.palette.mode).bg,
        overflow: 'hidden'
      }}>
      <SplashBackdrop />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1
        }}>
        <AnimatedRabexMark size={60} />
      </Box>
    </Box>
  );
};

export default RouteFallback;
